import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { hongKongSymbol } from './hk';
import { worldSymbol } from './world';

export interface HistorySync {
  symbol: string;
  state: 'queued' | 'updating' | 'ready' | 'unavailable' | 'failed' | 'unsupported';
  target: string | null;
  latest: string | null;
  checked_at: string;
  revision?: string;
  inserted_rows: number;
  message: string;
}

export function historySymbolKey(symbol: string) {
  const world=worldSymbol(symbol); if(world)return world;
  const hk = hongKongSymbol(symbol); if (hk) return hk;
  const prefix = /^(sh|sz)(\d{6})$/i.exec(symbol);
  const suffix = /^(\d{6})\.(SH|SZ)$/i.exec(symbol);
  const market = prefix?.[1].toLowerCase() ?? suffix?.[2].toLowerCase();
  const code = prefix?.[2] ?? suffix?.[1];
  if (code && ((market === 'sh' && /^(000|51|52|56|58)/.test(code))
    || (market === 'sz' && /^(399|15|16|18)/.test(code)))) return `${market}${code}`;
  return symbol;
}

export function historySyncQuery(symbol: string, period = 'daily') {
  return {
    queryKey: ['drawing-desk', 'history-sync', symbol, period] as const,
    queryFn: async ({ signal }: { signal: AbortSignal }) =>
      (await api.post<HistorySync>(`/../v2/drawing-desk/history-sync/${symbol}${period === 'daily' ? '' : `?period=${encodeURIComponent(period)}`}`, {}, { signal })).data,
    enabled: Boolean(symbol),
    staleTime: 0,
    refetchOnMount: 'always' as const,
    // Only the mounted asset requests work. Recent-stock GET prefetch never calls this.
    refetchInterval: (q: { state: { data?: HistorySync } }) =>
      q.state.data?.state === 'queued' || q.state.data?.state === 'updating' ? 2000 : 60_000,
    retry: 1,
  };
}

export function useHistorySync(symbol: string, period = 'daily') {
  const qc = useQueryClient();
  const query = useQuery(historySyncQuery(symbol, period));
  const applied = useRef('');
  useEffect(() => {
    const update = query.data;
    if (!update || historySymbolKey(update.symbol) !== historySymbolKey(symbol) || !['ready','unavailable','failed'].includes(update.state)) return;
    const stamp = `${symbol}:${period}:${update.target}:${update.latest}:${update.revision ?? ''}:${update.state}`;
    if (applied.current === stamp) return;
    applied.current = stamp;
    // Includes every period and off-screen cached canvas of this stock. An old
    // stock finishing later never overwrites the newly selected stock's data.
    void qc.invalidateQueries({ queryKey: ['drawing-desk','canvas',symbol] });
  }, [symbol, period, query.data, qc]);
  return query;
}

export function HistorySyncStatus({ symbol, data, failed, checking, retry }: {
  symbol: string; data?: HistorySync; failed: boolean; checking: boolean; retry: () => void;
}) {
  const current = data && historySymbolKey(data.symbol) === historySymbolKey(symbol) ? data : undefined;
  const active = current?.state === 'queued' || current?.state === 'updating';
  const text = failed ? '无法检查更新，将自动重试' : !current || (checking && !active) ? '正在检查最新交易日…' : current.message;
  return <div role="status" aria-live="polite" aria-busy={active || (!current && checking)}
    data-history-sync={failed ? 'failed' : current?.state ?? 'checking'}
    className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border/50 bg-card px-3 py-2 text-xs">
    <span className={failed || current?.state === 'failed' || current?.state === 'unavailable' ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}>
      {symbol} · {text}
    </span>
    {current?.target && <span className="text-muted-foreground">{checking && !active ? '上次核对目标' : '目标'} {current.target} · 当前数据 {current.latest ?? '暂无'}</span>}
    {(failed || current?.state === 'failed' || current?.state === 'unavailable') &&
      <button type="button" className="ml-auto text-primary" onClick={retry}>重新检查</button>}
  </div>;
}
